import { Template } from "@/types/template";
import useSWR from "swr";
import { useMemo, useState } from "react";
import { getTagLabel, ListTemplateCard } from "@/components/launch/ListTemplateCard";
import { bool, nonFalse } from "@/lib/utils";
import { useRouter } from "next/router";
import { ScrollToTop } from "@/components/landing/ScrollToTop";
import { cn } from "@/lib/utils";
import { FlaskConical, Search, SearchX, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { parseAsBoolean, parseAsString, useQueryState } from "nuqs";
import { MotionConfig, motion, useReducedMotion } from "motion/react";
import type { ReactNode } from "react";
import { useMediaQuery } from "@/components/useMediaQuery";

const priorityTags = ["oss", "popular", "template"];

/** Ease-out-quart — matches the template view page reveal exactly. */
const EASE_OUT = [0.22, 1, 0.36, 1] as const;

/** Staggers the page-load reveal of its direct children. */
const revealContainer = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08, delayChildren: 0.04 } }
};

/**
 * A keyboard-accessible filter toggle; reflects pressed state for screen readers.
 * Chips sharing a `layoutId` animate a single highlight that slides to the active one.
 */
const FilterChip = ({
  active,
  onClick,
  layoutId,
  children,
  "aria-label": ariaLabel
}: {
  active: boolean;
  onClick: () => void;
  layoutId?: string;
  children: ReactNode;
  "aria-label"?: string;
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    aria-label={ariaLabel}
    className={cn(
      "relative inline-flex shrink-0 items-center gap-1.5 rounded-md border px-2.5 py-1 text-sm transition-colors",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      active
        ? "border-primary text-primary-foreground"
        : "border-border text-foreground hover:bg-muted",
      active && !layoutId && "bg-primary"
    )}
  >
    {active && layoutId && (
      <motion.span
        layoutId={layoutId}
        transition={{ duration: 0.25, ease: EASE_OUT }}
        className="absolute -inset-px rounded-md bg-primary"
      />
    )}
    <span className="relative z-10 inline-flex items-center gap-1.5">{children}</span>
  </button>
);

export const ListTemplates = ({ templates: initialTemplates }: { templates: Template[] }) => {
  const router = useRouter();
  const skipCache = bool(router.query.skipCache);
  const { data: templates } = useSWR<Template[]>(
    `/api/template?skipCache=${skipCache}`,
    null,
    {
      fallbackData: initialTemplates
    }
  );
  // Shallow URL state: filtering happens client-side, so no server round-trip
  // or scroll reset — tags/experimental feel as instant as search.
  const [experimental, setExperimental] = useQueryState(
    "experimental",
    parseAsBoolean.withDefault(false)
  );
  const [selectedTag, setSelectedTag] = useQueryState("tag", parseAsString);
  const [search, setSearch] = useQueryState("search", parseAsString.withDefault(""));

  const [allTags, setAllTags] = useState(false);
  const isMobile = useMediaQuery("(max-width: 639px)");
  const maxShow = isMobile ? 2 : 5;

  const reduceMotion = useReducedMotion();
  const reveal = reduceMotion
    ? { hidden: { opacity: 1 }, show: { opacity: 1 } }
    : {
        hidden: { opacity: 0, y: 14 },
        show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: EASE_OUT } }
      };

  const tags = useMemo(() => {
    const tags = new Set<string>(
      templates
        ?.filter(t => (experimental ? true : !t.experimental))
        .map(t => t.tags || [])
        .flat()
    );
    if (selectedTag) {
      tags.add(selectedTag);
    }
    priorityTags.forEach(tag => tags.add(tag));
    const sorted = Array.from(tags).sort();
    return [
      ...priorityTags,
      selectedTag && priorityTags.includes(selectedTag) ? undefined : selectedTag,
      ...sorted.filter(t => !priorityTags.includes(t) && t !== selectedTag)
    ]
      .filter(nonFalse)
      .filter(t => t !== "");
  }, [templates, experimental, selectedTag]);

  const filteredTemplates = templates?.filter(template => {
    const experimentalMatch = experimental ? true : !template.experimental;
    const tagMatch = selectedTag ? template.tags?.includes(selectedTag) : true;
    return experimentalMatch && tagMatch;
  });

  const searchedTemplates = useMemo(() => {
    if (!search) {
      return filteredTemplates;
    }
    const q = search.toLowerCase();
    return filteredTemplates?.filter(
      template =>
        template.name.toLowerCase().includes(q) ||
        template.description?.toLowerCase().includes(q) ||
        template.tags?.some(tag => tag.toLowerCase().includes(q)) ||
        template.intro.toLowerCase().includes(q) ||
        template.id.toLowerCase().includes(q)
    );
  }, [filteredTemplates, search]);

  return (
    <MotionConfig reducedMotion="user">
      <motion.div
        className="mx-auto max-w-5xl px-4 pt-12 md:pt-20"
        variants={revealContainer}
        initial="hidden"
        animate="show"
      >
        <ScrollToTop />
        <motion.h1
          variants={reveal}
          className="text-balance text-center font-serif text-[clamp(2.25rem,5.5vw,56px)] font-normal leading-[1.05] tracking-tight"
        >
          Templates
        </motion.h1>
        <motion.p
          variants={reveal}
          className="mx-auto mt-3 max-w-xl text-balance text-center leading-relaxed text-secondary-foreground"
        >
          Deploy a new application in just a few clicks. Launch a new project from a
          boilerplate, or run an open-source application on a new or existing server.
        </motion.p>

        {/* Search */}
        <motion.div variants={reveal} className="relative mx-auto mt-6 w-full max-w-md">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 size-5 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search templates…"
            aria-label="Search templates"
            className="px-10"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <X className="size-4" strokeWidth={1.5} />
            </button>
          )}
        </motion.div>

        {/* Filters: tags on the left, experimental toggle on the right */}
        {tags.length > 0 && (
          <motion.div
            variants={reveal}
            className="mt-6 -mx-4 flex items-center gap-2 overflow-x-auto border-b px-4 pb-5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0"
          >
            <FilterChip
              active={!selectedTag}
              layoutId="tagFilter"
              onClick={() => setSelectedTag(null)}
            >
              All
            </FilterChip>
            {tags.slice(0, allTags ? tags.length : maxShow).map(tag => (
              <FilterChip
                key={tag}
                active={selectedTag === tag}
                layoutId="tagFilter"
                onClick={() => {
                  setSelectedTag(selectedTag === tag ? null : tag);
                  setAllTags(false);
                }}
              >
                {getTagLabel(tag)}
              </FilterChip>
            ))}
            {tags.length > maxShow && (
              <button
                type="button"
                onClick={() => setAllTags(!allTags)}
                className="shrink-0 text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
              >
                {allTags ? "Show less" : `Show ${tags.length - maxShow} more`}
              </button>
            )}
            <div className="ml-auto shrink-0">
              <FilterChip
                active={experimental}
                onClick={() => setExperimental(!experimental)}
                aria-label="Experimental"
              >
                <FlaskConical className="size-4" strokeWidth={1.5} />
                <span className="hidden sm:inline">Experimental</span>
              </FilterChip>
            </div>
          </motion.div>
        )}

        <motion.div variants={reveal}>
          {searchedTemplates && searchedTemplates.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-20 text-center">
              <SearchX className="size-8 text-muted-foreground" strokeWidth={1.5} />
              <p className="text-lg text-foreground">
                {search
                  ? `No templates match “${search}”`
                  : selectedTag
                    ? `No templates tagged “${getTagLabel(selectedTag)}”`
                    : "No templates match your filters"}
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                {search && (
                  <Button variant="outline" size="sm" onClick={() => setSearch("")}>
                    Clear search
                  </Button>
                )}
                {!experimental && (
                  <Button variant="outline" size="sm" onClick={() => setExperimental(true)}>
                    Include experimental
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div className="mx-auto grid max-w-sm gap-4 pt-6 sm:max-w-none sm:grid-cols-2 lg:grid-cols-3">
              {searchedTemplates?.map(template => (
                <ListTemplateCard key={template.id} template={template} />
              ))}
            </div>
          )}
        </motion.div>
      </motion.div>
    </MotionConfig>
  );
};
